import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import pricingService, { CartCalculation } from '../services/pricing';
import nuclearCartCleanup from '../security/cart-nuclear-cleanup';

// SECURITY: Emergency override function to validate ANY data before storage
function securityValidateCartData(data: any[]): any[] {
  console.warn('🔒 SECURITY VALIDATION: Checking cart data before storage');
  
  const validated = data.map(item => {
    // ONLY allow id and quantity - STRIP EVERYTHING ELSE
    const cleanItem = {
      id: item.id,
      quantity: item.quantity
    };
    
    // Log if we're stripping manipulation data
    if (item.price !== undefined || item.final_price !== undefined || item.name !== undefined) {
      console.error('🚨 SECURITY: STRIPPING MANIPULATION DATA FROM CART ITEM!', item);
      console.error('🔒 SECURITY: CLEANED ITEM:', cleanItem);
    }
    
    return cleanItem;
  });
  
  return validated;
}

// Minimal cart item - only store what cannot be manipulated
export interface CartItemStorage {
  id: number;
  quantity: number;
}

// Full cart item with secure pricing from backend
export interface CartItem {
  id: number;
  name: string;
  sku: string;
  quantity: number;
  original_price: number;
  final_price: number;
  has_discount: boolean;
  item_total: number;
  categories?: string[];
  images?: string[];
}

interface CartContextType {
  items: CartItem[];
  totalItems: number;
  cartCalculation: CartCalculation | null;
  isCalculating: boolean;
  currentCampaignCode: string | null;
  addItem: (productId: number, quantity?: number) => void;
  removeItem: (productId: number) => void;
  updateQuantity: (productId: number, quantity: number) => void;
  clearCart: () => void;
  isInCart: (productId: number) => boolean;
  getItemQuantity: (productId: number) => number;
  calculateSecureCart: (shippingMethod?: 'delivery' | 'pickup', campaignCode?: string) => Promise<CartCalculation | null>;
  setCurrentCampaignCode: (code: string | null) => void;
  forceSecureCart: () => void; // Force clean any manipulated data
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: ReactNode;
}

const CART_STORAGE_KEY = 'lillaflora_cart';

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  // Only store IDs and quantities - never prices!
  const [storedItems, setStoredItems] = useState<CartItemStorage[]>([]);
  const [items, setItems] = useState<CartItem[]>([]);
  const [cartCalculation, setCartCalculation] = useState<CartCalculation | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [currentCampaignCode, setCurrentCampaignCode] = useState<string | null>(null);
  const lastShippingMethodRef = useRef<'delivery' | 'pickup'>('delivery');
  const lastCampaignCodeRef = useRef<string | null>(null);

  // Load cart IDs and quantities from localStorage on initial load
  useEffect(() => {
    try {
      const storedCart = localStorage.getItem(CART_STORAGE_KEY);
      if (storedCart) {
        const parsedCart = JSON.parse(storedCart);
        
        // SECURITY: Migrate old cart structure to secure structure
        if (parsedCart.length > 0 && parsedCart[0].price !== undefined) {
          console.warn('SECURITY: Migrating old cart structure with manipulable prices to secure structure');
          // Extract only IDs and quantities from old structure
          const secureCart: CartItemStorage[] = parsedCart.map((item: any) => ({
            id: item.id,
            quantity: item.quantity
          }));
          setStoredItems(secureCart);
          // Immediately save the secure structure
          localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(secureCart));
        } else {
          // Already secure structure
          setStoredItems(parsedCart as CartItemStorage[]);
        }
      }
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
      // Clear corrupted cart data
      localStorage.removeItem(CART_STORAGE_KEY);
    }
  }, []);

  // Save only IDs and quantities to localStorage - SECURITY: Strip any price data
  useEffect(() => {
    try {
      // SECURITY: NEVER save items array with prices - ONLY minimal data
      const preValidatedData = storedItems.map(item => {
        // EXTRA SECURITY: Validate that we're only storing safe data
        if (typeof item.id !== 'number' || typeof item.quantity !== 'number') {
          console.error('SECURITY ERROR: Invalid cart item data detected', item);
          return null;
        }
        return {
          id: item.id,
          quantity: item.quantity
          // SECURITY: Explicitly exclude ALL other fields
        };
      }).filter(item => item !== null);
      
      // EMERGENCY SECURITY: Double-validate through security function
      const secureStorageData = securityValidateCartData(preValidatedData);

      // SECURITY: Double-check that no price data exists in what we're saving
      const hasAnyPriceData = secureStorageData.some((item: any) =>
        item.hasOwnProperty('price') ||
        item.hasOwnProperty('final_price') ||
        item.hasOwnProperty('original_price') ||
        item.hasOwnProperty('name') ||
        item.hasOwnProperty('sku')
      );

      if (hasAnyPriceData) {
        console.error('🚨 SECURITY ALERT: Attempted to save price data to localStorage!');
        return; // Don't save anything if price data detected
      }

      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(secureStorageData));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  }, [storedItems]);

  const addItem = (productId: number, quantity: number = 1) => {
    setStoredItems(currentItems => {
      const existingItem = currentItems.find(item => item.id === productId);

      if (existingItem) {
        // Update quantity of existing item
        return currentItems.map(item =>
          item.id === productId
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        // Add new item (only ID and quantity)
        return [...currentItems, { id: productId, quantity }];
      }
    });
  };

  const removeItem = (productId: number) => {
    setStoredItems(currentItems => currentItems.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId: number, quantity: number) => {
    if (quantity <= 0) {
      removeItem(productId);
      return;
    }

    setStoredItems(currentItems =>
      currentItems.map(item =>
        item.id === productId
          ? { ...item, quantity }
          : item
      )
    );
  };

  const clearCart = () => {
    setStoredItems([]);
    setItems([]);
    setCartCalculation(null);
    setCurrentCampaignCode(null);
    localStorage.removeItem(CART_STORAGE_KEY);
  };

  // SECURITY: Force clean any manipulated localStorage data
  const forceSecureCart = () => {
    try {
      const storedCart = localStorage.getItem(CART_STORAGE_KEY);
      if (storedCart) {
        const parsedCart = JSON.parse(storedCart);

        // Check if data has been manipulated (contains price fields)
        const hasManipulatedData = parsedCart.some((item: any) =>
          item.price !== undefined ||
          item.final_price !== undefined ||
          item.original_price !== undefined ||
          item.name !== undefined
        );

        if (hasManipulatedData) {
          console.error('🚨 SECURITY BREACH: Manipulated cart data detected! Activating nuclear cleanup...');

          // NUCLEAR OPTION: Complete cleanup
          const nukedCart = nuclearCartCleanup();
          setStoredItems(nukedCart);

          // Alert user about security action
          alert('SECURITY ALERT: Price manipulation detected and blocked. Cart has been secured.');
          console.error('☢️ NUCLEAR CLEANUP ACTIVATED: All price data destroyed');
        }
      }
    } catch (error) {
      console.error('Error cleaning cart data:', error);
      // If anything goes wrong, clear everything
      localStorage.removeItem(CART_STORAGE_KEY);
      setStoredItems([]);
    }
  };

  // Clear any invalid items that can't be priced
  const clearInvalidItems = () => {
    if (storedItems.length > 0 && cartCalculation && cartCalculation.items.length === 0) {
      // Only clear if we have stored items but calculation returned empty (invalid products)
      console.warn('Cart: Invalid products detected, clearing from storage');
      setStoredItems([]);
      localStorage.removeItem(CART_STORAGE_KEY);
    }
  };

  const isInCart = (productId: number) => {
    return storedItems.some(item => item.id === productId);
  };

  const getItemQuantity = (productId: number) => {
    const item = storedItems.find(item => item.id === productId);
    return item ? item.quantity : 0;
  };

  const totalItems = storedItems.reduce((total, item) => total + item.quantity, 0);

  // Secure cart calculation function - always uses backend pricing
  const calculateSecureCart = useCallback(async (shippingMethod?: 'delivery' | 'pickup', campaignCode?: string | null): Promise<CartCalculation | null> => {
    if (storedItems.length === 0) {
      setCartCalculation(null);
      setItems([]);
      return null;
    }

    setIsCalculating(true);

    try {
      const methodToUse = shippingMethod || lastShippingMethodRef.current || 'delivery';
      lastShippingMethodRef.current = methodToUse;

      let normalizedCampaignCode: string | null | undefined;
      if (campaignCode !== undefined) {
        normalizedCampaignCode = campaignCode ? campaignCode.trim().toUpperCase() : null;
        lastCampaignCodeRef.current = normalizedCampaignCode;
      } else {
        normalizedCampaignCode = lastCampaignCodeRef.current;
      }

      // Send only IDs and quantities - backend will fetch secure pricing
      const calculation = await pricingService.calculateCart(
        storedItems,
        methodToUse,
        normalizedCampaignCode || undefined
      );
      setCartCalculation(calculation);
      setItems(calculation.items); // Update items with secure pricing from backend
      return calculation;
    } catch (error) {
      console.error('Failed to calculate secure cart totals:', error);
      // Clear items if calculation fails - no fallback to client-side pricing!
      setItems([]);
      setCartCalculation(null);
      return null;
    } finally {
      setIsCalculating(false);
    }
  }, [storedItems]);

  // Auto-calculate when stored items change
  useEffect(() => {
    if (storedItems.length > 0) {
      calculateSecureCart();
    } else {
      setCartCalculation(null);
      setItems([]);
    }
  }, [storedItems, calculateSecureCart]);

  useEffect(() => {
    lastCampaignCodeRef.current = currentCampaignCode ? currentCampaignCode.trim().toUpperCase() : null;
  }, [currentCampaignCode]);

  // SECURITY: Aggressively check for cart manipulation
  useEffect(() => {
    // Initial immediate check
    forceSecureCart();
    
    // Check more frequently
    const interval = setInterval(() => {
      forceSecureCart();
    }, 1000); // Check every 1 second
    
    // Also check on any storage event
    const handleStorageChange = () => {
      console.warn('🛡️ SECURITY: Storage change detected, checking for manipulation...');
      forceSecureCart();
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const value: CartContextType = {
    items,
    totalItems,
    cartCalculation,
    isCalculating,
    currentCampaignCode,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    isInCart,
    getItemQuantity,
    calculateSecureCart,
    setCurrentCampaignCode,
    forceSecureCart
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartProvider;
